import { useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import { Button, Dialog, Layout, Text } from '@loadsmart/loadsmart-ui'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { Icon } from 'core/ui/components/icon/Icon'
import { createDownloadLink, triggerDownload } from 'core/utils/link'

import { type Attachment, hasRfpAttachments } from '../domain/Attachment'
import { downloadAttachments } from '../use-cases/downloadAttachments'

const Wrapper = styled(Layout.Box)`
  flex: 1;
  margin-top: ${getToken('space-l')};
`

type AttachmentsProps = {
  attachments: Attachment[]
  rfpId: string
  rfpName: string
}

export function Attachments({ attachments, rfpId, rfpName }: AttachmentsProps) {
  const hasAttachments = hasRfpAttachments(attachments)
  const [isOpen, setIsOpen] = useState(hasAttachments)

  async function showErrorDownload() {
    toast.error('Error downloading attachments. Please try again.')
  }

  async function handleDownload(onSuccess?: Function) {
    try {
      const attachmentBlob = await downloadAttachments(rfpId)
      const link = createDownloadLink(attachmentBlob, rfpName, '_attachments.zip')
      triggerDownload(link)
      onSuccess?.()
    } catch (error) {
      showErrorDownload()
    }
  }

  async function handleDialogDownload() {
    handleDownload(() => setIsOpen(false))
  }

  if (!hasAttachments) return null

  const message =
    'Review the important information regarding this bid contained within the attachments'

  return (
    <>
      <Dialog open={isOpen} scale="small">
        <Dialog.Header>Attachments</Dialog.Header>
        <Dialog.Body>{message}</Dialog.Body>
        <Dialog.ActionConfirm onConfirm={handleDialogDownload} />
        <Dialog.ActionCancel onCancel={() => setIsOpen(false)} />
      </Dialog>
      <Wrapper
        padding="l"
        background="neutral-white"
        borderRadius="s"
        borderColor="neutral-light"
        borderWidth="thin"
      >
        <Layout.Group justify="space-between" align="center">
          <Text>{message}</Text>
          <Button leading={<Icon name="download" />} scale="small" onClick={() => handleDownload()}>
            Download attachments
          </Button>
        </Layout.Group>
      </Wrapper>
    </>
  )
}
